/** The computed properties that decide where a textarea's text wraps and where each character lands. A
 *  mirror that copies them lays the same text out the same way. */
const MIRRORED = [
  'direction', 'box-sizing', 'width',
  'border-top-width', 'border-right-width', 'border-bottom-width', 'border-left-width', 'border-style',
  'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'font-style', 'font-variant', 'font-weight', 'font-stretch', 'font-size', 'font-size-adjust', 'line-height',
  'font-family', 'font-feature-settings', 'font-kerning', 'font-variation-settings',
  'text-align', 'text-transform', 'text-indent', 'text-rendering',
  'letter-spacing', 'word-spacing', 'tab-size', 'word-break',
] as const;

/** The viewport rectangle of the boundary before character `index` of `field`'s value: zero wide, one line
 *  high, already shifted by the field's own scroll. A textarea exposes no geometry for its text, so this
 *  is the mirror-element technique: an invisible div with the field's typography and box, holding the text
 *  up to `index`, a marker, then the rest of the text (so the word around `index` wraps as it does in the
 *  field). The marker's offset and line box are read and the mirror removed again: one synchronous layout
 *  per call. */
export function caretRect(field: HTMLTextAreaElement, index: number): DOMRect {
  const style = getComputedStyle(field);
  const document = field.ownerDocument;
  const mirror = document.createElement('div');
  for (const property of MIRRORED) mirror.style.setProperty(property, style.getPropertyValue(property));
  mirror.style.position = 'absolute';
  mirror.style.top = '0';
  mirror.style.left = '0';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.overflowWrap = 'break-word';
  // A field that scrolls gives its scrollbar part of the width the text wraps in; the mirror must too.
  mirror.style.overflowY = field.scrollHeight > field.clientHeight ? 'scroll' : 'hidden';
  const marker = document.createElement('span');
  // A word joiner has no width and adds no break opportunity, but gives the marker a line box to measure.
  marker.textContent = '\u2060';
  mirror.append(field.value.slice(0, index), marker, field.value.slice(index));
  document.body.append(mirror);
  try {
    const box = field.getBoundingClientRect();
    // `offsetLeft/Top` start at the mirror's padding edge; `clientLeft/Top` add the field's border back.
    return new DOMRect(
      box.left + field.clientLeft + marker.offsetLeft - field.scrollLeft,
      box.top + field.clientTop + marker.offsetTop - field.scrollTop,
      0,
      marker.offsetHeight,
    );
  } finally {
    mirror.remove();
  }
}
